import clsx from 'clsx';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Brightness2Icon from '@material-ui/icons/Brightness2';
import WbSunnyIcon from '@material-ui/icons/WbSunny';
import { useThemeToggle } from './useThemeToggle';
import { MoreThemesTooltip } from './MoreThemesTooltip';

const useStyles = makeStyles<Theme>(theme => ({
  expandedContainer: {
    padding: theme.spacing(0.75, 2),
    paddingBottom: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
  },
  segmentedControl: {
    display: 'flex',
    width: '100%',
    height: 32,
    borderRadius: 16,
    backgroundColor:
      theme.palette.navigation.navItem?.hoverBackground ?? '#404040',
    padding: 2,
    boxSizing: 'border-box',
  },
  segment: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    borderRadius: 14,
    cursor: 'pointer',
    transition: 'background-color 200ms ease, opacity 200ms ease',
    color: theme.palette.navigation.color,
    opacity: 0.6,
    '&:hover': {
      opacity: 0.85,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  segmentActive: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.navigation.background
        : '#ffffff',
    opacity: 1,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    cursor: 'default',
    pointerEvents: 'none',
  },
  segmentLabel: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'inherit',
  },
  moonIcon: {
    fontSize: 16,
    color: theme.palette.navigation.color,
  },
  sunIcon: {
    fontSize: 16,
    color: '#F5A623',
  },
}));

export const ExpandedThemeToggle = () => {
  const classes = useStyles();
  const {
    isDark,
    setThemeVariant,
    showExtendedTooltip,
    handleMouseEnter,
    handleMouseLeave,
    handlePopperMouseEnter,
    handlePopperMouseLeave,
    anchorRef,
  } = useThemeToggle();

  return (
    <div
      ref={anchorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box className={classes.expandedContainer}>
        <div className={classes.segmentedControl}>
          <Tooltip
            title={isDark ? 'Toggle light mode' : ''}
            placement="top"
            PopperProps={{
              style: { zIndex: 1600 },
            }}
          >
            <div
              className={clsx(
                classes.segment,
                !isDark && classes.segmentActive,
              )}
              onClick={() => setThemeVariant('light')}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setThemeVariant('light')}
            >
              <WbSunnyIcon
                className={classes.sunIcon}
                data-testid="WbSunnyIcon"
              />
              <Typography className={classes.segmentLabel}>Light</Typography>
            </div>
          </Tooltip>
          <Tooltip
            title={!isDark ? 'Toggle dark mode' : ''}
            placement="top"
            PopperProps={{
              style: { zIndex: 1600 },
            }}
          >
            <div
              className={clsx(classes.segment, isDark && classes.segmentActive)}
              onClick={() => setThemeVariant('dark')}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setThemeVariant('dark')}
            >
              <Brightness2Icon
                className={classes.moonIcon}
                data-testid="Brightness2Icon"
              />
              <Typography className={classes.segmentLabel}>Dark</Typography>
            </div>
          </Tooltip>
        </div>
      </Box>

      <MoreThemesTooltip
        open={showExtendedTooltip}
        anchorEl={anchorRef.current}
        onMouseEnter={handlePopperMouseEnter}
        onMouseLeave={handlePopperMouseLeave}
      />
    </div>
  );
};
